import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { RefreshCw, ArrowLeftRight, X, Check } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { ShiftSwapRequestDialog } from './ShiftSwapRequestDialog';

export const ShiftSwapRequests = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: swapSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['shift-swap-settings-check'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log('🔄 No user found for swap settings check');
        return null;
      }

      console.log('🔄 Checking swap settings for user:', user.id);

      // Get user's admin
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('created_by')
        .eq('id', user.id)
        .single();

      console.log('🔄 User profile:', profile, 'Error:', profileError);

      if (!profile?.created_by) {
        console.log('🔄 No created_by found, user might be admin themselves');
        // If no created_by, user might be admin - check their own settings
        const { data: adminSettings } = await supabase
          .from('shift_swap_settings')
          .select('is_enabled')
          .eq('admin_id', user.id)
          .maybeSingle();
        
        console.log('🔄 Admin own settings:', adminSettings);
        return adminSettings;
      }

      const { data, error } = await supabase
        .from('shift_swap_settings')
        .select('is_enabled')
        .eq('admin_id', profile.created_by)
        .maybeSingle();

      console.log('🔄 Swap settings for admin', profile.created_by, ':', data, 'Error:', error);

      return data;
    },
  });

  const { data: currentUser } = useQuery({
    queryKey: ['current-user-for-swaps'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      console.log('👤 Current user for swaps:', user);
      return user;
    },
  });

  const { data: myRequests, isLoading: isLoadingRequests } = useQuery({
    queryKey: ['my-swap-requests'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('shift_swap_requests')
        .select(`
          *,
          requester:requester_guard_id(full_name),
          target:target_guard_id(full_name),
          requester_shift:requester_shift_id(start_date, end_date, team_id),
          target_shift:target_shift_id(start_date, end_date, team_id)
        `)
        .or(`requester_guard_id.eq.${user.id},target_guard_id.eq.${user.id}`)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: swapSettings?.is_enabled === true,
  });

  const cancelMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from('shift_swap_requests')
        .update({ status: 'cancelled' })
        .eq('id', requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-swap-requests'] });
      toast({
        title: 'Request cancelled',
        description: 'Your swap request has been cancelled.',
      });
    },
  });

  const acceptRequestMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update the existing request to matched status
      const { error } = await supabase
        .from('shift_swap_requests')
        .update({
          status: 'matched',
          matched_at: new Date().toISOString(),
          responded_by: user.id,
          responded_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-swap-requests'] });
      queryClient.invalidateQueries({ queryKey: ['matched-swap-requests'] });
      toast({
        title: 'Request accepted',
        description: 'Request is now matched and waiting for admin approval.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to accept request',
        variant: 'destructive',
      });
    },
  });

  const rejectRequestMutation = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase
        .from('shift_swap_requests')
        .update({ status: 'rejected' })
        .eq('id', requestId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-swap-requests'] });
      toast({
        title: 'Request rejected',
        description: 'You have rejected this swap request.',
      });
    },
  });

  if (isLoadingSettings) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!swapSettings?.is_enabled) {
    const isCurrentUserAdmin = currentUser && (
      currentUser.user_metadata?.role === 'admin' ||
      currentUser.user_metadata?.role === 'super_admin'
    );

    return (
      <Card>
        <CardHeader>
          <CardTitle>Shift Swap Requests</CardTitle>
          <CardDescription>
            {isCurrentUserAdmin ? (
              <div className="flex items-center justify-between">
                <span>Shift swap feature is currently disabled</span>
                <Button
                  size="sm"
                  onClick={() => window.location.href = '/settings'}
                  className="ml-4"
                >
                  Enable in Settings
                </Button>
              </div>
            ) : (
              'Shift swap feature is currently disabled by your administrator'
            )}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
      pending: "secondary",
      matched: "default",
      approved: "default",
      rejected: "destructive",
      cancelled: "outline",
    };

    return <Badge variant={variants[status] || "outline"}>{status}</Badge>;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Shift Swap Requests</CardTitle>
            <CardDescription>
              Request to swap your shifts with other guards
            </CardDescription>
          </div>
          <Button onClick={() => setIsDialogOpen(true)}>
            <ArrowLeftRight className="mr-2 h-4 w-4" />
            New Swap Request
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoadingRequests ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : myRequests && myRequests.length > 0 ? (
          <div className="space-y-4">
            {myRequests.map((request: any) => {
              console.log('🔍 Request:', {
                requestId: request.id,
                targetGuardId: request.target_guard_id,
                requesterGuardId: request.requester_guard_id,
                currentUserId: currentUser?.id,
                status: request.status
              });
              
              const isIncoming = currentUser && request.target_guard_id === currentUser.id;
              const isOutgoing = currentUser && request.requester_guard_id === currentUser.id;
              
              console.log('🔍 Flags:', { isIncoming, isOutgoing });

              return (
                <div
                  key={request.id}
                  className="flex items-start justify-between rounded-lg border p-4"
                >
                  <div className="space-y-2 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{request.requester.full_name}</span>
                      <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{request.target.full_name}</span>
                      {isIncoming && request.status === 'pending' && (
                        <Badge variant="default" className="bg-blue-500">Εισερχόμενο</Badge>
                      )}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      <div>
                        Δίνει: {format(parseISO(request.requester_shift.start_date), 'PPp')} - {format(parseISO(request.requester_shift.end_date), 'p')}
                      </div>
                      <div>
                        Παίρνει: {format(parseISO(request.target_shift.start_date), 'PPp')} - {format(parseISO(request.target_shift.end_date), 'p')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(request.status)}
                      {request.status === 'matched' && (
                        <span className="text-xs text-muted-foreground">
                          Αναμονή έγκρισης από διαχειριστή
                        </span>
                      )}
                      {request.status === 'approved' && (
                        <span className="text-xs text-green-600">
                          Εγκρίθηκε - Η αλλαγή πραγματοποιήθηκε
                        </span>
                      )}
                      {request.status === 'rejected' && request.rejection_reason && (
                        <div className="text-xs text-destructive">
                          <div className="font-medium">Απορρίφθηκε</div>
                          <div className="mt-1">{request.rejection_reason}</div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {isIncoming && request.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => acceptRequestMutation.mutate(request.id)}
                          disabled={acceptRequestMutation.isPending}
                        >
                          <Check className="mr-2 h-4 w-4" />
                          Αποδοχή
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => rejectRequestMutation.mutate(request.id)}
                          disabled={rejectRequestMutation.isPending}
                        >
                          <X className="mr-2 h-4 w-4" />
                          Απόρριψη
                        </Button>
                      </>
                    )}
                    {isOutgoing && request.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => cancelMutation.mutate(request.id)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            No swap requests yet
          </div>
        )}
      </CardContent>

      <ShiftSwapRequestDialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />
    </Card>
  );
};
